import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import {getDefaultOrdersLazyData} from '@Components/event-ticketing/event-ticketing.types';
import {LoadingStates} from '@Utils/loading.util';
import {eventDonationsExtraReducers} from './event-donations-extra-reducers';
import {getActiveDonationsList} from './event-donations.helper';
import type {EventDonationsState} from './event-donations.types';

const getDefaultEventDonationsState = (): EventDonationsState => {
  return {
    defaultDonations: getDefaultOrdersLazyData(),
    filteredDonations: getDefaultOrdersLazyData(),
    search: '',
    liveSearchTerm: '',
    eventHashedID: '',
    hasDonationsFetchFailed: false,
  };
};

const eventDonationsSlice = createSlice({
  name: 'eventDonations',
  initialState: getDefaultEventDonationsState(),
  reducers: {
    // A submitted search is a fresh query: the searched list is reset and the lazy-load fetches its first page. The
    // cached unfiltered list stays (clearing the search shows it again).
    setDonationsSearch: (state, {payload}: PayloadAction<string>) => {
      state.search = payload;
      state.liveSearchTerm = payload;
      state.filteredDonations = getDefaultOrdersLazyData();
      state.hasDonationsFetchFailed = false;
    },
    // The term as typed narrows the loaded rows in place; clearing the field drops a submitted search immediately.
    setDonationsLiveSearchTerm: (state, {payload}: PayloadAction<string>) => {
      state.liveSearchTerm = payload;
      if (payload === '' && state.search !== '') {
        state.search = '';
        state.filteredDonations = getDefaultOrdersLazyData();
        state.hasDonationsFetchFailed = false;
      }
    },
    // Nudges the active list so the lazy-load effect fetches the next page.
    triggerDonationsLazyLoadOnScroll: (state) => {
      const activeDonations = getActiveDonationsList(state, state.search);
      if (activeDonations.currentPageNumber < activeDonations.lastPage && activeDonations.loadingState !== LoadingStates.LOADING) {
        activeDonations.loadingState = LoadingStates.NOT_LOADED;
      }
    },
  },
  extraReducers: eventDonationsExtraReducers,
});

export const {setDonationsSearch, setDonationsLiveSearchTerm, triggerDonationsLazyLoadOnScroll} = eventDonationsSlice.actions;
export const eventDonationsReducer = eventDonationsSlice.reducer;
